import { createClient } from 'contentful';
import { canonicalizePublicMediaUrl } from './mediaUrl';

const DEFAULT_LOCALE = 'en-US';

function safeJsonParse(value, fallback) {
  if (!value) {
    return fallback;
  }

  if (typeof value === 'object') {
    return value;
  }

  try {
    return JSON.parse(value);
  } catch (error) {
    return fallback;
  }
}

function isLocaleKey(key) {
  return /^[a-z]{2}(?:-[A-Za-z]{2,})?$/.test(key);
}

function hasContent(value) {
  if (Array.isArray(value)) {
    return value.length > 0;
  }

  return value !== undefined && value !== null && value !== '';
}

function getLocalizedValue(value, preferredLocale = DEFAULT_LOCALE) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return value;
  }

  if (Object.prototype.hasOwnProperty.call(value, preferredLocale)) {
    return value[preferredLocale];
  }

  const localeKeys = Object.keys(value).filter(isLocaleKey);
  if (localeKeys.length) {
    const populatedKey = localeKeys.find((key) => hasContent(value[key])) || localeKeys[0];
    return value[populatedKey];
  }

  return value;
}

function getEntryField(entry, fieldName, preferredLocale = DEFAULT_LOCALE) {
  return getLocalizedValue(entry?.fields?.[fieldName], preferredLocale);
}

function firstDefined(...values) {
  return values.find((value) => hasContent(value));
}

function normalizeStringArray(value) {
  const localizedValue = getLocalizedValue(value);

  if (!localizedValue) {
    return [];
  }

  if (Array.isArray(localizedValue)) {
    return localizedValue
      .map((item) => getLocalizedValue(item))
      .filter((item) => typeof item === 'string' && item.trim())
      .map((item) => item.trim());
  }

  if (typeof localizedValue === 'string' && localizedValue.trim()) {
    return [localizedValue.trim()];
  }

  return [];
}

function toTitleCase(value) {
  return String(value)
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function slugify(value) {
  return String(value || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function normalizeUrl(value) {
  const localizedValue = getLocalizedValue(value);

  if (!localizedValue) {
    return '';
  }

  if (typeof localizedValue === 'string') {
    if (localizedValue.startsWith('http')) {
      return canonicalizePublicMediaUrl(localizedValue);
    }

    if (localizedValue.startsWith('//')) {
      return canonicalizePublicMediaUrl(`https:${localizedValue}`);
    }

    return canonicalizePublicMediaUrl(localizedValue);
  }

  const file = getLocalizedValue(localizedValue?.fields?.file || localizedValue?.file);
  const fileUrl = getLocalizedValue(file?.url) || getLocalizedValue(localizedValue?.url) || '';

  if (!fileUrl) {
    return '';
  }

  return normalizeUrl(fileUrl);
}

function normalizeAssetUrl(asset) {
  return normalizeUrl(asset);
}

function getPrimaryTagLabel(entry) {
  const tagId = entry?.metadata?.tags?.[0]?.sys?.id;
  return tagId ? toTitleCase(tagId) : '';
}

function getTimestamp(value) {
  const timestamp = new Date(value || 0).getTime();
  return Number.isNaN(timestamp) ? 0 : timestamp;
}

function extractJobBodyHighlights(body, limit = 3) {
  if (typeof body !== 'string' || !body.trim()) {
    return [];
  }

  const lines = body.split(/\r?\n/).map((line) => line.trim());
  const highlights = [];
  let isTargetSection = false;
  let currentBullet = '';

  const pushCurrentBullet = () => {
    if (currentBullet.trim()) {
      highlights.push(currentBullet.replace(/\s+/g, ' ').trim());
      currentBullet = '';
    }
  };

  lines.forEach((line) => {
    const headingMatch = line.match(/^#{1,6}\s+(.+)$/);

    if (headingMatch) {
      pushCurrentBullet();
      isTargetSection = /what\s+you['’]?ll\s+do|responsibilities|role/i.test(headingMatch[1]);
      return;
    }

    if (!isTargetSection || highlights.length >= limit) {
      return;
    }

    const bulletMatch = line.match(/^[•●*+-]\s*(.+)$/);

    if (bulletMatch) {
      pushCurrentBullet();
      currentBullet = bulletMatch[1];
      return;
    }

    if (currentBullet && line) {
      currentBullet = `${currentBullet} ${line}`;
    }
  });

  pushCurrentBullet();

  return highlights.slice(0, limit);
}

function getRichTextNodeText(node) {
  if (!node) {
    return '';
  }

  if (typeof node === 'string') {
    return node;
  }

  if (node.nodeType === 'text') {
    return node.value || '';
  }

  if (!Array.isArray(node.content)) {
    return '';
  }

  return node.content
    .map(getRichTextNodeText)
    .filter(Boolean)
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function applyRichTextMarks(value, marks = []) {
  return marks.reduce((markedValue, mark) => {
    switch (mark.type) {
      case 'bold':
        return `**${markedValue}**`;
      case 'italic':
        return `_${markedValue}_`;
      case 'code':
        return `\`${markedValue}\``;
      case 'underline':
        return `<u>${markedValue}</u>`;
      default:
        return markedValue;
    }
  }, value);
}

function indentMarkdown(value) {
  return value
    .split('\n')
    .map((line) => (line ? `  ${line}` : line))
    .join('\n');
}

function richTextChildrenToMarkdown(node) {
  return (node.content || []).map(richTextNodeToMarkdown).join('');
}

function richTextListItemToMarkdown(node) {
  return (node.content || [])
    .map((childNode) => {
      if (childNode.nodeType === 'paragraph') {
        return richTextChildrenToMarkdown(childNode).trim();
      }

      if (childNode.nodeType === 'unordered-list' || childNode.nodeType === 'ordered-list') {
        return `\n${indentMarkdown(richTextNodeToMarkdown(childNode))}`;
      }

      return richTextNodeToMarkdown(childNode);
    })
    .filter(Boolean)
    .join('\n');
}

function richTextNodeToMarkdown(node) {
  if (!node) {
    return '';
  }

  if (typeof node === 'string') {
    return node;
  }

  if (node.nodeType === 'text') {
    return applyRichTextMarks(node.value || '', node.marks || []);
  }

  switch (node.nodeType) {
    case 'document':
      return (node.content || []).map(richTextNodeToMarkdown).filter(Boolean).join('\n\n').trim();
    case 'paragraph':
      return richTextChildrenToMarkdown(node).trim();
    case 'heading-1':
    case 'heading-2':
    case 'heading-3':
    case 'heading-4':
    case 'heading-5':
    case 'heading-6': {
      const level = Number(node.nodeType.split('-')[1]);
      return `${'#'.repeat(level)} ${richTextChildrenToMarkdown(node).trim()}`;
    }
    case 'unordered-list':
      return (node.content || [])
        .map((childNode) => `- ${richTextListItemToMarkdown(childNode)}`)
        .join('\n');
    case 'ordered-list':
      return (node.content || [])
        .map((childNode, childIndex) => `${childIndex + 1}. ${richTextListItemToMarkdown(childNode)}`)
        .join('\n');
    case 'blockquote':
      return (node.content || [])
        .map(richTextNodeToMarkdown)
        .filter(Boolean)
        .join('\n\n')
        .split('\n')
        .map((line) => `> ${line}`)
        .join('\n');
    case 'hr':
      return '---';
    case 'hyperlink': {
      const text = richTextChildrenToMarkdown(node).trim();
      const uri = node.data?.uri;
      return text && uri ? `[${text}](${uri})` : text;
    }
    case 'entry-hyperlink':
    case 'asset-hyperlink':
      return richTextChildrenToMarkdown(node).trim();
    case 'list-item':
      return richTextListItemToMarkdown(node);
    default:
      if (Array.isArray(node.content)) {
        return node.content.map(richTextNodeToMarkdown).filter(Boolean).join('\n\n');
      }

      return '';
  }
}

function richTextToMarkdown(value) {
  const localizedValue = getLocalizedValue(value);

  if (!localizedValue) {
    return '';
  }

  if (typeof localizedValue === 'string') {
    return localizedValue;
  }

  if (typeof localizedValue === 'object' && localizedValue.nodeType) {
    return richTextNodeToMarkdown(localizedValue);
  }

  return '';
}

function getRichTextParagraphs(value) {
  const document = getLocalizedValue(value);

  if (!document || typeof document !== 'object' || !Array.isArray(document.content)) {
    return [];
  }

  return document.content
    .filter((node) => node.nodeType === 'paragraph' || /^heading-[1-6]$/.test(node.nodeType))
    .map(getRichTextNodeText)
    .filter(Boolean);
}

function getRichTextSummary(value) {
  return getRichTextParagraphs(value).find((paragraph) => paragraph.length > 40) || '';
}

function collectRichTextListItems(node, items = []) {
  if (!node || typeof node !== 'object') {
    return items;
  }

  if (node.nodeType === 'list-item') {
    const itemText = getRichTextNodeText(node);

    if (itemText) {
      items.push(itemText);
    }

    return items;
  }

  if (Array.isArray(node.content)) {
    node.content.forEach((childNode) => collectRichTextListItems(childNode, items));
  }

  return items;
}

function extractRichTextHighlights(value, limit = 3) {
  return collectRichTextListItems(getLocalizedValue(value)).slice(0, limit);
}

function formatJobLocation(value) {
  const location = getLocalizedValue(value);

  if (!location) {
    return '';
  }

  if (typeof location === 'string') {
    return location;
  }

  if (typeof location === 'object' && location.lat !== undefined && location.lon !== undefined) {
    const latitude = Number(location.lat);
    const longitude = Number(location.lon);

    if (!Number.isNaN(latitude) && !Number.isNaN(longitude)) {
      return `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`;
    }
  }

  return '';
}

function isCareerListingEntry(entry) {
  const contentTypeId = entry?.sys?.contentType?.sys?.id;

  if (contentTypeId === 'jobs') {
    return true;
  }

  const keywords = normalizeStringArray(getEntryField(entry, 'keywords')).map((keyword) => keyword.toLowerCase());

  return keywords.some((keyword) => keyword.includes('job') || keyword.includes('career'));
}

function normalizeJobEntries(entries) {
  const seenSlugs = new Set();

  return entries
    .filter(isCareerListingEntry)
    .map(mapJobRole)
    .filter((job) => {
      const key = job.slug || job.title;

      if (!key || seenSlugs.has(key)) {
        return false;
      }

      seenSlugs.add(key);
      return true;
    })
    .sort((left, right) => getTimestamp(right.publishedAt) - getTimestamp(left.publishedAt));
}

export function mapJobRole(entry) {
  const jobTitle = firstDefined(getEntryField(entry, 'jobTitle'), getEntryField(entry, 'title'), '');
  const jobId = getEntryField(entry, 'jobId') || '';
  const jobDescription = getEntryField(entry, 'jobDescription');
  const body = firstDefined(richTextToMarkdown(getEntryField(entry, 'body')), richTextToMarkdown(jobDescription), getRichTextNodeText(jobDescription)) || '';
  const expectations = firstDefined(
    safeJsonParse(getEntryField(entry, 'expectationsJson'), []),
    extractRichTextHighlights(jobDescription),
    extractJobBodyHighlights(body),
    []
  );
  const department = firstDefined(getEntryField(entry, 'department'), getEntryField(entry, 'team')) || '';
  const schedule = firstDefined(getEntryField(entry, 'schedule'), getEntryField(entry, 'employmentType')) || '';
  const keywords = normalizeStringArray(getEntryField(entry, 'keywords'));
  const slug = firstDefined(
    getEntryField(entry, 'slug'),
    [slugify(jobTitle), jobId].filter(Boolean).join('-'),
    entry.sys.id
  ) || entry.sys.id;
  const description = firstDefined(
    getEntryField(entry, 'description'),
    getEntryField(entry, 'summary'),
    getRichTextSummary(jobDescription)
  ) || '';

  return {
    slug,
    title: jobTitle,
    jobId,
    team: department,
    department,
    location: formatJobLocation(getEntryField(entry, 'location')),
    employmentType: schedule,
    schedule,
    summary: description,
    description,
    publishedAt: firstDefined(getEntryField(entry, 'jobReleaseDate'), getEntryField(entry, 'publishedAt'), getEntryField(entry, 'date')) || '',
    body,
    jobDescriptionRich: jobDescription || null,
    keywords: keywords.length ? keywords : [department, schedule].filter(Boolean),
    expectations,
    callout: getEntryField(entry, 'callout') || (jobId ? `Job ID ${jobId}` : ''),
  };
}

export function mapResourceArticle(entry) {
  return {
    slug: getEntryField(entry, 'slug') || entry.sys.id,
    title: getEntryField(entry, 'title') || '',
    category: firstDefined(getEntryField(entry, 'category'), getPrimaryTagLabel(entry), 'Resource'),
    publishedAt: firstDefined(getEntryField(entry, 'publishedAt'), getEntryField(entry, 'date'), ''),
    featuredImage: normalizeAssetUrl(getEntryField(entry, 'featuredImage')),
    summary: firstDefined(getEntryField(entry, 'summary'), getEntryField(entry, 'description'), ''),
    body: getEntryField(entry, 'body') || '',
    keywords: normalizeStringArray(getEntryField(entry, 'keywords')),
  };
}

export function mapCaseStudy(entry) {
  const title = getEntryField(entry, 'title') || '';
  const slug = firstDefined(getEntryField(entry, 'slug'), slugify(title), entry.sys.id) || entry.sys.id;
  const bodyRich = getEntryField(entry, 'body');
  const summary = getRichTextSummary(bodyRich) || '';
  const featuredImage = normalizeAssetUrl(getEntryField(entry, 'featuredImage'));
  const keywords = normalizeStringArray(getEntryField(entry, 'keywords'));
  const date = firstDefined(getEntryField(entry, 'date'), getEntryField(entry, 'publishedAt'), '') || '';

  return {
    slug,
    title,
    summary,
    featuredImage,
    heroImage: featuredImage,
    date,
    publishedAt: date,
    bodyRichText: bodyRich || null,
    body: richTextToMarkdown(bodyRich) || '',
    keywords,
    sector: keywords[0] || '',
    videoUrl: getEntryField(entry, 'videoUrl') || '',
  };
}

export function mapHeroVideo(entry) {
  return {
    heroSection: getEntryField(entry, 'heroSection') || '',
    cdnVideoFileName: getEntryField(entry, 'cdnVideoFileName') || '',
    videoRuntime: getEntryField(entry, 'videoRuntime') || 5,
  };
}

export function mapR1000ProductVideo(entry) {
  return {
    cdnVideoFileName: getEntryField(entry, 'cdnVideoFileName') || '',
    videoRuntime: getEntryField(entry, 'videoRuntime') || 5,
    productVideoTab: getEntryField(entry, 'ProductVideoTab') || '',
  };
}

export function mapR1000HomeDualVideo(entry) {
  return {
    cdnVideoFileName: getEntryField(entry, 'cdnVideoFileName') || '',
    videoRuntime: getEntryField(entry, 'videoRuntime') || 5,
    panel: getEntryField(entry, 'VideoPanel') || '',
  };
}

export function mapCredential(entry) {
  return {
    name: getEntryField(entry, 'name') || '',
    logo: normalizeAssetUrl(getEntryField(entry, 'logo')),
    note: getEntryField(entry, 'note') || '',
    showName: getEntryField(entry, 'showName') || false,
  };
}

export function mapTeamMember(entry) {
  const name = getEntryField(entry, 'name') || '';
  const groupValue = getEntryField(entry, 'group');
  // group is Boolean: true = executive, false/null = technical
  const group = groupValue === true ? 'executive' : 'technical';
  const bioRich = getEntryField(entry, 'bio');
  const bio = getRichTextNodeText(bioRich) || richTextToMarkdown(bioRich) || '';
  const credentialAssets = getEntryField(entry, 'credentialsLogo') || [];
  const credentials = Array.isArray(credentialAssets)
    ? credentialAssets
        .map((asset) => ({ logo: normalizeAssetUrl(asset), name: getLocalizedValue(asset?.fields?.title) || '' }))
        .filter((c) => c.logo)
    : [];

  return {
    slug: firstDefined(slugify(name), entry.sys.id) || entry.sys.id,
    name,
    role: getEntryField(entry, 'role') || '',
    group,
    bioRichText: bioRich || null,
    bio,
    photo: normalizeAssetUrl(getEntryField(entry, 'headshot')),
    linkedInUrl: getEntryField(entry, 'linkedinUrl') || '',
    credentials,
  };
}

function getContentfulConfig() {
  const space = process.env.REACT_APP_CONTENTFUL_SPACE_ID;
  const environment = process.env.REACT_APP_CONTENTFUL_ENVIRONMENT || 'master';
  const usePreview = process.env.REACT_APP_CONTENTFUL_USE_PREVIEW === 'true';
  const accessToken =
    (usePreview ? process.env.REACT_APP_CONTENTFUL_PREVIEW_TOKEN : '') ||
    process.env.REACT_APP_CONTENTFUL_DELIVERY_TOKEN ||
    process.env.REACT_APP_CONTENTFUL_API_KEY;

  if (!space || !accessToken) {
    return null;
  }

  return {
    space,
    environment,
    accessToken,
    host: usePreview ? 'preview.contentful.com' : 'cdn.contentful.com',
  };
}

export function hasContentfulConfig() {
  return Boolean(getContentfulConfig());
}

function buildClient() {
  const config = getContentfulConfig();

  if (!config) {
    return null;
  }

  return createClient(config);
}

async function fetchEntries(client, contentType, options = {}) {
  try {
    const response = await client.getEntries({
      content_type: contentType,
      include: 2,
      limit: 1000,
      ...options,
    });
    return response.items;
  } catch (error) {
    console.warn(`[Contentful] Failed to fetch "${contentType}":`, error.message);
    return [];
  }
}

async function fetchAssetsByTitlePrefix(client, prefix) {
  try {
    const response = await client.getAssets({ 'fields.title[match]': prefix.replace(/\/$/, ''), limit: 100 });
    return response.items
      .filter((asset) => {
        const title = getLocalizedValue(asset.fields?.title) || '';
        return title.startsWith(prefix) || title === prefix.replace(/\/$/, '');
      })
      .map((asset) => {
        const title = getLocalizedValue(asset.fields?.title) || '';
        const label = title.replace(prefix, '');
        const description = getLocalizedValue(asset.fields?.description) || '';
        const image = normalizeAssetUrl(asset);
        const file = getLocalizedValue(asset.fields?.file);
        const contentType = getLocalizedValue(file?.contentType) || '';
        return { label, image, alt: label, body: description, contentType };
      });
  } catch (error) {
    console.warn(`[Contentful] Failed to fetch assets with prefix "${prefix}":`, error.message);
    return [];
  }
}

export async function fetchContentfulSiteContent() {
  const client = buildClient();

  if (!client) {
    return null;
  }

  const [jobsEntries, resourceEntries, caseStudyEntries, teamMemberEntries, faqEntries, privacyPolicyEntries, toolBitOptions, toolOptions, accessoryOptions, layoutImages, netshapeUIImages, netshapeImages, heroVideoEntries, r1000ProductVideoEntries, r1000HomeDualVideoEntries] = await Promise.all([
    fetchEntries(client, 'jobs', { order: '-fields.jobReleaseDate' }),
    fetchEntries(client, 'title', { order: '-fields.date' }),
    fetchEntries(client, 'caseStudies'),
    fetchEntries(client, 'team'),
    fetchEntries(client, 'faq'),
    fetchEntries(client, 'privacyPolicy', { order: '-fields.date', limit: 1 }),
    fetchAssetsByTitlePrefix(client, 'toolBitOptions/'),
    fetchAssetsByTitlePrefix(client, 'toolOptions/'),
    fetchAssetsByTitlePrefix(client, 'AccessoryOptions/'),
    fetchAssetsByTitlePrefix(client, 'layout/'),
    fetchAssetsByTitlePrefix(client, 'NetshapeUI'),
    fetchAssetsByTitlePrefix(client, 'netshape/'),
    fetchEntries(client, 'heroVideos'),
    fetchEntries(client, 'r1000ProductVideos'),
    fetchEntries(client, 'r1000homeDualVideo'),
  ]);

  const latestPrivacyPolicy = privacyPolicyEntries[0] || null;
  const privacyPolicyRichText = latestPrivacyPolicy ? getLocalizedValue(latestPrivacyPolicy?.fields?.privacyPolicy) || null : null;
  const privacyPolicyDate = latestPrivacyPolicy ? getEntryField(latestPrivacyPolicy, 'date') || '' : '';

  return {
    pages: {},
    collections: {
      privacyPolicyContent: privacyPolicyRichText
        ? { richText: privacyPolicyRichText, date: privacyPolicyDate }
        : null,
      jobs: normalizeJobEntries(jobsEntries),
      resourceArticles: resourceEntries
        .map(mapResourceArticle)
        .sort((left, right) => getTimestamp(right.publishedAt) - getTimestamp(left.publishedAt)),
      caseStudies: caseStudyEntries
        .map(mapCaseStudy)
        .sort((left, right) => getTimestamp(right.publishedAt) - getTimestamp(left.publishedAt)),
      teamMembers: teamMemberEntries.map(mapTeamMember),
      faqItems: faqEntries.map((e) => ({
        slug: e.sys.id,
        question: e.fields.question,
        answer: e.fields.answer,
        category: Array.isArray(e.fields.category) ? e.fields.category[0] : e.fields.category,
      })),
      toolBitOptions,
      toolOptions,
      accessoryOptions,
      layoutImages,
      netshapeUIImages,
      netshapeImages,
      heroVideos: heroVideoEntries.map(mapHeroVideo),
      r1000ProductVideos: r1000ProductVideoEntries.map(mapR1000ProductVideo),
      r1000HomeDualVideo: r1000HomeDualVideoEntries.map(mapR1000HomeDualVideo),
    },
  };
}

// --- Per-collection lazy fetcher with pagination ---

const COLLECTION_CONFIG = {
  resourceArticles: { contentType: 'title', order: '-fields.date', mapper: mapResourceArticle },
  jobs: { contentType: 'jobs', order: '-fields.jobReleaseDate', mapper: mapJobRole, postProcess: normalizeJobEntries },
  caseStudies: { contentType: 'caseStudies', mapper: mapCaseStudy },
  teamMembers: { contentType: 'team', mapper: mapTeamMember },
  faqItems: {
    contentType: 'faq',
    mapper: (e) => ({
      slug: e.sys.id,
      question: e.fields.question,
      answer: e.fields.answer,
      category: Array.isArray(e.fields.category) ? e.fields.category[0] : e.fields.category,
    }),
  },
  heroVideos: { contentType: 'heroVideos', mapper: mapHeroVideo },
  r1000ProductVideos: { contentType: 'r1000ProductVideos', mapper: mapR1000ProductVideo },
  r1000HomeDualVideo: { contentType: 'r1000homeDualVideo', mapper: mapR1000HomeDualVideo },
  privacyPolicyContent: {
    contentType: 'privacyPolicy',
    order: '-fields.date',
    mapper: (e) => ({
      richText: getLocalizedValue(e.fields?.privacyPolicy) || null,
      date: getEntryField(e, 'date') || '',
    }),
  },
};

/**
 * Fetch a single collection with pagination.
 * @param {string} collectionKey - One of the keys in COLLECTION_CONFIG
 * @param {{ skip?: number, limit?: number }} options
 * @returns {Promise<{ items: Array, total: number }>}
 */
export async function fetchCollection(collectionKey, { skip = 0, limit = 6 } = {}) {
  const config = COLLECTION_CONFIG[collectionKey];
  if (!config) {
    throw new Error(`Unknown collection: ${collectionKey}`);
  }

  const client = buildClient();
  if (!client) {
    return { items: [], total: 0 };
  }

  const queryOptions = { skip, limit, include: 2 };
  if (config.order) {
    queryOptions.order = config.order;
  }

  try {
    const response = await client.getEntries({
      content_type: config.contentType,
      ...queryOptions,
    });

    let items = response.items.map(config.mapper);
    if (config.postProcess) {
      items = config.postProcess(response.items);
    }

    return { items, total: response.total };
  } catch (error) {
    console.warn(`[Contentful] Failed to fetch "${collectionKey}":`, error.message);
    return { items: [], total: 0 };
  }
}

/**
 * Fetch assets by title prefix (for product page modules).
 * No pagination needed — these are small sets.
 */
export async function fetchAssetCollection(prefix) {
  const client = buildClient();
  if (!client) return [];
  return fetchAssetsByTitlePrefix(client, prefix);
}
